import { useState } from "react";
import { Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useBotShield, BotHoneypot } from "@/lib/botShield";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/**
 * Newsletter opt-in card. CASL-compliant — express consent collected via the
 * email field + the submit button click. The backend stores a timestamped
 * consent record + a one-click unsubscribe token.
 *
 * Drop this anywhere — Landing footer, Blog index, any province page.
 */
export default function NewsletterSignup({ source = "newsletter", compact = false }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [touched, setTouched] = useState(false);
  const shield = useBotShield();

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());
  // Only surface the inline error once the user has actually typed
  // something and left the field. An empty, untouched (or blurred but
  // still empty) input should look neutral — hitting them with a red
  // "invalid email" state before they've typed a character feels
  // aggressive per Feb 2026 UX review.
  const showError = touched && email.length > 0 && !isValidEmail(email);

  const submit = async (e) => {
    e.preventDefault();
    const v = (email || "").trim();
    setTouched(true);
    if (!v || !isValidEmail(v)) {
      toast.error("A valid email address is required.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/newsletter/subscribe`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: v, source, ...shield.values() }),
      });
      if (!r.ok) throw new Error("subscribe-failed");
      setDone(true);
      toast.success("You're in — check your inbox for the welcome note.");
    } catch {
      toast.error("Could not subscribe. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className={`${compact ? "p-4" : "p-6"} rounded-2xl border border-emerald-200 bg-emerald-50/60`} data-testid="newsletter-done">
        <div className="text-sm font-bold text-emerald-700">You&apos;re subscribed</div>
        <div className="text-xs text-emerald-700/80 mt-1">A welcome note is on its way. Field notes will land in your inbox as soon as they&apos;re ready. Unsubscribe any time.</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`${compact ? "p-4" : "p-5 sm:p-6"} rounded-[24px] border border-emerald-100 bg-gradient-to-br from-white to-emerald-50/40 shadow-sm`} data-testid="newsletter-form">
      <BotHoneypot {...shield.fieldProps} />
      <div className="flex items-center gap-2 text-emerald-700">
        <Mail className="w-4 h-4"/>
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold">Field notes · 4,200+ Canadian landlords</div>
      </div>
      <h3 className={`font-display ${compact ? "text-lg" : "text-xl sm:text-2xl"} font-extrabold text-slate-950 mt-2 leading-tight`}>
        Get the 2026 <span className="text-emerald-700">CRA landlord checklist</span> free.
      </h3>
      <p className="text-sm text-slate-700 mt-2 leading-relaxed">
        1-page T776 checklist + a weekly field-note (rent-law changes, tax deadlines, real-tenant stories). Written by landlords, for landlords. <b>Delivered instantly.</b> One-click unsubscribe. Never spam.
      </p>
      {/* Concrete trust signals — replaces the vague "no spam" with data. */}
      <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px] text-slate-600">
        <li className="inline-flex items-center gap-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-emerald-600 flex-shrink-0"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd"/></svg>
          Instant PDF download
        </li>
        <li className="inline-flex items-center gap-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-emerald-600 flex-shrink-0"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd"/></svg>
          Weekly · 3-min read
        </li>
        <li className="inline-flex items-center gap-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-emerald-600 flex-shrink-0"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd"/></svg>
          CASL compliant
        </li>
        <li className="inline-flex items-center gap-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-emerald-600 flex-shrink-0"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd"/></svg>
          One-click unsubscribe
        </li>
      </ul>
      <div className="mt-4 space-y-2">
        <div>
          <label htmlFor="newsletter-email-input" className="sr-only">Your email</label>
          <input
            id="newsletter-email-input"
            type="email"
            required
            aria-required="true"
            aria-invalid={showError ? "true" : "false"}
            aria-describedby="newsletter-email-help"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="you@example.com"
            data-testid="newsletter-email"
            className={`w-full px-5 py-3.5 rounded-full border outline-none text-base transition-colors bg-white
              ${showError
                ? "border-red-400 bg-red-50/60 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                : "border-emerald-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"}`}
          />
          {showError && (
            <div id="newsletter-email-help" data-testid="newsletter-email-error"
              className="text-[11px] text-red-600 mt-1 pl-3">
              Please enter a valid email address.
            </div>
          )}
        </div>
        <button type="submit" disabled={busy} data-testid="newsletter-submit"
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "16px 20px",
            borderRadius: "9999px",
            backgroundColor: "#059669",
            color: "#ffffff",
            fontWeight: 800,
            fontSize: "16px",
            border: "0",
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.6 : 1,
            boxShadow: "0 10px 25px -5px rgba(5,150,105,0.4)",
            transition: "background-color 0.2s ease",
          }}
          onMouseEnter={(e) => { if (!busy) e.currentTarget.style.backgroundColor = "#047857"; }}
          onMouseLeave={(e) => { if (!busy) e.currentTarget.style.backgroundColor = "#059669"; }}
          className="group">
          {busy ? "Sending…" : <>Send me the checklist <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1"/></>}
        </button>
      </div>
      <div className="text-[11px] text-slate-500 mt-3">
        Consent stored per CASL. Zero third-party sharing. Read our <a href="/privacy" className="underline underline-offset-2 hover:text-emerald-700">privacy policy</a>.
      </div>
    </form>
  );
}
